import { useEffect, useState } from 'react';
import { AuthShell } from '@/components/auth/AuthShell';
import { useAuth } from '@/hooks/useAuth';

/**
 * Verify email landing · /verify · slot slot_supabase_migration · SM.3.
 *
 * The confirmation email link is processed by supabase-js (detectSessionInUrl):
 * a valid link establishes a session, an expired/invalid one does not. This
 * screen derives its state from the auth state rather than a router-passed
 * `state` prop — the prop is kept in the interface for router compatibility.
 */

export type VerifyEmailState = 'loading' | 'success' | 'invalid' | 'used' | 'error';

export interface VerifyEmailScreenProps {
  state: VerifyEmailState;
  onSuccess: () => void;
  onResend: () => void;
  onLogin: () => void;
}

export function VerifyEmailScreen({ onSuccess, onResend, onLogin }: VerifyEmailScreenProps) {
  const auth = useAuth();
  const [revealed, setRevealed] = useState(false);
  const verified = !auth.isLoading && auth.isAuthed;

  useEffect(() => {
    if (verified) {
      const id = window.setTimeout(() => {
        setRevealed(true);
      }, 800);
      const advance = window.setTimeout(() => {
        onSuccess();
      }, 1600);
      return () => {
        window.clearTimeout(id);
        window.clearTimeout(advance);
      };
    }
    return undefined;
  }, [verified, onSuccess]);

  if (auth.isLoading) {
    return (
      <AuthShell title="Проверяем ссылку…" subtitle="Это займёт пару секунд.">
        <div className="auth-pending">
          <div className="auth-pending__icon" aria-hidden="true">
            🔄
          </div>
        </div>
      </AuthShell>
    );
  }

  if (verified) {
    return (
      <AuthShell
        title={revealed ? 'Готово ✓' : 'Email подтверждён'}
        subtitle="Сейчас откроется Studio."
        pulseBuddy
      >
        <div className="auth-pending">
          <div className="auth-pending__icon" aria-hidden="true">
            ✓
          </div>
        </div>
      </AuthShell>
    );
  }

  // No session established — the link is invalid, expired, or already used.
  return (
    <AuthShell
      title="Ссылка устарела"
      subtitle="Email-ссылки действуют 24 часа · запроси новую или войди с паролем."
    >
      <div className="auth-pending">
        <div className="auth-pending__icon" aria-hidden="true">
          ⏱
        </div>
        <button type="button" className="btn btn--primary" onClick={onResend}>
          Прислать новую ссылку
        </button>
        <button type="button" className="btn btn--ghost" onClick={onLogin}>
          Войти с email и паролем
        </button>
      </div>
    </AuthShell>
  );
}
